import React, { useState, FormEvent } from 'react';
import { API_URL } from '../services/api/client';
import { twMerge } from 'tailwind-merge';
import { Button } from './ui';

interface NewsletterSignupProps {
  source?: string;
  light?: boolean;
}

const NewsletterSignup: React.FC<NewsletterSignupProps> = ({ source = 'website', light = false }) => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!email.trim() || loading) return;

    setLoading(true);
    setMessage(null);

    try {
      const res = await fetch(`${API_URL}/newsletter/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), source }),
      });

      const data = await res.json();

      if (res.ok && data.success) {
        setMessage({ type: 'success', text: 'Check your email to confirm your subscription.' });
        setEmail('');
      } else {
        setMessage({ type: 'error', text: data.error || 'Something went wrong. Please try again.' });
      }
    } catch {
      setMessage({ type: 'error', text: 'Network error. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-[480px] mx-auto">
      <h3 className={twMerge(
        'text-lg font-semibold m-0 mb-2',
        light ? 'text-text-primary' : 'text-text-inverse',
      )}>
        Stay in the loop
      </h3>
      <p className={twMerge(
        'text-sm m-0 mb-4',
        light ? 'text-text-secondary' : 'text-white/70',
      )}>
        Product updates, D/s insights, and community highlights. Delivered occasionally.
      </p>

      <form className="flex gap-2 flex-wrap flex-col sm:flex-row" onSubmit={handleSubmit}>
        <input
          type="email"
          className={twMerge(
            'flex-1 min-w-0 sm:min-w-[200px] py-2.5 px-3.5 text-sm border rounded-lg outline-none transition-colors focus:border-accent',
            light
              ? 'bg-bg-primary text-text-primary border-border-default placeholder:text-text-tertiary'
              : 'bg-white/10 text-white border-white/20 placeholder:text-white/50',
          )}
          placeholder="Your email address"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          disabled={loading}
          aria-label="Email address for newsletter"
        />
        <Button
          type="submit"
          variant="primary"
          className="bg-aegean text-white whitespace-nowrap w-full sm:w-auto"
          disabled={loading || !email.trim()}
        >
          {loading ? 'Subscribing...' : 'Subscribe'}
        </Button>
      </form>

      <p className={twMerge(
        'text-xs mt-2 mb-0',
        light ? 'text-text-tertiary' : 'text-white/50',
      )}>
        No spam. Unsubscribe anytime.
      </p>

      {message && (
        <div className={twMerge(
          'py-2.5 px-3.5 rounded-lg text-sm mt-3 border',
          message.type === 'success'
            ? 'bg-success/15 text-success border-success/30'
            : 'bg-danger/15 text-danger border-danger/30',
        )}>
          {message.text}
        </div>
      )}
    </div>
  );
};

export default NewsletterSignup;
